import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import { useHover } from '@hooks/useHover';
import theme, { Theme } from '@themes/theme';

type HoverableDayCellProps = {
  /** Disables the grey01 hover tint (e.g. already-selected/range-endpoint cells). */
  hoverDisabled: boolean;
  onPress: () => void;
  /** Disables the TouchableOpacity itself. Most calendar grids only disable
   * the hover tint and leave the cell pressable (blank/out-of-range cells
   * are inert because onPress is a no-op there); pass this only when the
   * cell must reject presses outright (e.g. outside min/maxDate). */
  touchDisabled?: boolean;
  cursor?: 'pointer' | 'not-allowed' | 'default';
  accessibilityLabel?: string;
  /** Pixel border radius for the hover-tint clip. Defaults to a full pill
   * (theme.borderRadii.xxl); pass a smaller value to match a specific
   * circle's own radius, or theme.borderRadii.xs for a rectangular button. */
  radius?: number;
  /** Fixed box dimensions - only needed for non-circular callers like the
   * Clear button (day/month-nav cells size via their own `children`). This is
   * also the cell's touch/layout footprint (e.g. a grid column's width), so
   * `children` that must bleed past the cell (a continuous range band) stay
   * unclipped - only the hover tint itself is clipped, see `hoverWidth`. */
  width?: number;
  height?: number;
  /** Hover-tint circle's own size, when it's smaller than the touch/layout
   * box (review N2/round-3: a 48-wide grid column with a centered 40x40
   * circular tint, so the column stays aligned with its header and any
   * bleeding range-band content in `children` isn't clipped to 40 wide
   * too). Defaults to width/height - most callers want the tint to fill the
   * whole cell. */
  hoverWidth?: number;
  hoverHeight?: number;
  opacity?: number;
  children: React.ReactNode;
};

// TA-10950 #7/#26: grey01 hover on every selectable calendar date, month-nav
// cell, and Clear button. Every DatePicker/DateRangePicker grid variant
// re-implemented this same useHover + Box + TouchableOpacity shell
// independently (SonarCloud duplication on new code); this is the shared
// shell they all wrap their own content in. `cursor` is web-only (RN native
// doesn't support it) - gating it here keeps every caller correct on native
// without each one remembering to.
//
// TA-10950 review round 3: the hover tint's clip lives on its own decorative
// layer (absolutely positioned, centered, pointerEvents='none'), not on the
// outer touch/layout box - `children` render as normal siblings outside that
// clip. Clipping the whole cell previously broke any caller whose hover
// circle is smaller than its column width (DRP3's N2 fix) or whose range-band
// overlay deliberately bleeds past the cell into neighboring columns (DRP2,
// DateRangePickerWebV3).
export const HoverableDayCell: React.FC<HoverableDayCellProps> = ({
  hoverDisabled,
  onPress,
  touchDisabled = false,
  cursor,
  accessibilityLabel,
  radius = theme.borderRadii.xxl,
  width,
  height,
  hoverWidth,
  hoverHeight,
  opacity,
  children,
}) => {
  const { isHovered, hoverProps } = useHover({ disabled: hoverDisabled });

  return (
    <TouchableOpacity
      {...hoverProps}
      onPress={onPress}
      disabled={touchDisabled}
      accessibilityLabel={accessibilityLabel}
      style={[
        {
          width,
          height,
          alignItems: 'center',
          justifyContent: 'center',
          opacity,
        },
        Platform.OS === 'web' && cursor ? ({ cursor } as object) : undefined,
      ]}>
      <Box
        pointerEvents='none'
        style={StyleSheet.absoluteFillObject}
        alignItems='center'
        justifyContent='center'>
        <Box
          style={{
            // review round 4: a caller with no width/height (e.g. DRP2's
            // ClearDateButton, sized only by its own Text content) left this
            // explicit pixel size undefined, collapsing the tint to 0x0.
            // '100%' fills the decorative layer's own absoluteFillObject
            // parent instead, which is exactly the touch/layout box's
            // rendered size either way - matching pre-round-3 behavior where
            // the tint wrapped (and sized to) the cell's actual content.
            width: hoverWidth ?? width ?? '100%',
            height: hoverHeight ?? height ?? '100%',
            borderRadius: radius,
            overflow: 'hidden',
          }}
          backgroundColor={isHovered ? 'grey01' : undefined}
        />
      </Box>
      {children}
    </TouchableOpacity>
  );
};

type ClearDateButtonProps = {
  hoverDisabled: boolean;
  touchDisabled?: boolean;
  onPress: () => void;
  cursor?: 'pointer' | 'not-allowed' | 'default';
  width?: number;
  height: number;
  opacity?: number;
  /** Overrides the translated "Clear" label (e.g. a caller-supplied clearLabel prop). */
  label?: string;
  textColor?: keyof Theme['colors'];
};

// TA-10950 #7/#26: every DatePicker/DateRangePicker variant independently
// wrapped this exact rectangular Clear button (HoverableDayCell + labelSmall
// Text) in its own footer, differing only in the hoverDisabled/cursor/onPress
// logic each already passes through as props (SonarCloud duplication on new
// code - the shared HoverableDayCell shell didn't cover the repeated calling
// shape itself).
// TA-10950 review: DateRangePicker2/3's DayCell both independently computed
// this same "is this day one of the selected endpoints" chain (SonarCloud
// duplication on new code) - each file's own StyleSheet objects are distinct
// references, so the chain itself is the shared piece, not the styles.
type EndpointDayStyles = {
  daySelect: object;
  daySelectStart: object;
  todaySelectStart: object;
  daySelectEnd: object;
  todaySelectEnd: object;
};

export const isEndpointDayStyle = (
  dayStyle: object,
  styles: EndpointDayStyles
) =>
  dayStyle === styles.daySelect ||
  dayStyle === styles.daySelectStart ||
  dayStyle === styles.todaySelectStart ||
  dayStyle === styles.daySelectEnd ||
  dayStyle === styles.todaySelectEnd;

// A style pair for a "container + dot" overlay (e.g. the selected-day dot,
// or a range-endpoint cap) - `container` positions/shapes the overlay box,
// `dot` is the smaller filled circle/strike inside it.
type ContainerDotStyle = { container: object; dot: object };

type RangeDayCellProps = {
  day: number;
  dayStyle: object;
  /** styles.dayViewItem - the day-number text's own positioning style. */
  dayViewItemStyle: object;
  textVariant: 'metadata' | 'webBodySecondary';
  hoverDisabled: boolean;
  onPress: () => void;
  cursor: 'default' | 'pointer';
  radius: number;
  width: number;
  height: number;
  /** Hover-tint circle size, when smaller than width/height (review round 3:
   * DRP3's column must stay 48 wide to match its header/range bars, with
   * only the hover tint itself shrunk to a 40x40 circle). Defaults to
   * width/height - see HoverableDayCell's own `hoverWidth`/`hoverHeight`. */
  hoverWidth?: number;
  hoverHeight?: number;
  /** DRP3 sizes its inner content box independently of the hover circle
   * (48x40 vs the hover circle's 40x40); DRP2 lets it size to its content. */
  innerBoxSize?: { width: number; height: number };
  marginHorizontal: 'xxs' | 's' | 'none';
  // Each prop below is the exact style the caller already resolved for its
  // own row-boundary/selection-state branching (which differs between
  // DateRangePicker2 and DateRangePicker3 - see TA-10950 PR #6025 review) -
  // `undefined` means "don't render this overlay".
  rangeCapStartStyle?: object;
  rangeCapEndStyle?: object;
  selectedDotStyle?: ContainerDotStyle;
  startCapStyle?: ContainerDotStyle;
  endCapStyle?: ContainerDotStyle;
  inSelectionStyle?: object;
};

// TA-10950 review: DateRangePicker2/3's DayCell independently rendered this
// same "hover circle wrapping a day number + up to 5 conditional
// range/selection overlays" shape (SonarCloud duplication on new code). The
// two files pick different styles for a few row-boundary edge cases (a
// pre-existing difference, not something this extraction should flatten) -
// callers resolve their own styles and pass the result in, so behavior is
// unchanged.
export const RangeDayCell: React.FC<RangeDayCellProps> = ({
  day,
  dayStyle,
  dayViewItemStyle,
  textVariant,
  hoverDisabled,
  onPress,
  cursor,
  radius,
  width,
  height,
  hoverWidth,
  hoverHeight,
  innerBoxSize,
  marginHorizontal,
  rangeCapStartStyle,
  rangeCapEndStyle,
  selectedDotStyle,
  startCapStyle,
  endCapStyle,
  inSelectionStyle,
}) => (
  <HoverableDayCell
    hoverDisabled={hoverDisabled}
    onPress={onPress}
    cursor={cursor}
    radius={radius}
    width={width}
    height={height}
    hoverWidth={hoverWidth}
    hoverHeight={hoverHeight}>
    <Box
      width={innerBoxSize?.width}
      height={innerBoxSize?.height}
      alignItems='center'
      marginHorizontal={marginHorizontal}
      justifyContent='center'>
      <Text variant={textVariant} style={[dayStyle, dayViewItemStyle]}>
        {day > 0 ? day : ' '}
      </Text>
      {rangeCapStartStyle && <Box style={rangeCapStartStyle} />}
      {rangeCapEndStyle && <Box style={rangeCapEndStyle} />}
      {selectedDotStyle && (
        <Box style={selectedDotStyle.container}>
          <Box style={selectedDotStyle.dot} />
        </Box>
      )}
      {startCapStyle && (
        <Box style={startCapStyle.container}>
          <Box style={startCapStyle.dot} />
        </Box>
      )}
      {endCapStyle && (
        <Box style={endCapStyle.container}>
          <Box style={endCapStyle.dot} />
        </Box>
      )}
      {inSelectionStyle && <Box style={inSelectionStyle} />}
    </Box>
  </HoverableDayCell>
);

export const ClearDateButton: React.FC<ClearDateButtonProps> = ({
  hoverDisabled,
  touchDisabled,
  onPress,
  cursor,
  width,
  height,
  opacity,
  label,
  textColor = 'textPrimary',
}) => {
  const { t } = useTranslation();
  const clearLabel = label ?? t('shared:clear');

  return (
    <HoverableDayCell
      hoverDisabled={hoverDisabled}
      touchDisabled={touchDisabled}
      onPress={onPress}
      cursor={cursor}
      accessibilityLabel={clearLabel}
      radius={theme.borderRadii.xs}
      width={width}
      height={height}
      opacity={opacity}>
      <Text color={textColor} variant='labelSmall'>
        {clearLabel}
      </Text>
    </HoverableDayCell>
  );
};
